import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { galleryUi } from '../data/galleryData';

const GalleryLightbox = ({ photos, index, language, onClose, onChange }) => {
  const touchX = useRef(null);
  const n = photos.length;
  const go = (d) => onChange((index + d + n) % n);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const photo = photos[index];
  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center animate-lightbox-in"
      onClick={onClose}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
        touchX.current = null;
      }}
      data-testid="gallery-lightbox"
    >
      <button onClick={onClose} aria-label={galleryUi.close[language]} data-testid="gallery-lightbox-close"
        className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-[#c9a227] text-white hover:text-[#121212] flex items-center justify-center transition-colors z-10">
        <X size={22} />
      </button>
      {n > 1 && <button onClick={(e) => { e.stopPropagation(); go(-1); }} aria-label={galleryUi.prev[language]} data-testid="gallery-lightbox-prev"
        className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-[#c9a227] text-white hover:text-[#121212] flex items-center justify-center transition-colors z-10">
        <ChevronLeft size={24} />
      </button>}
      {n > 1 && <button onClick={(e) => { e.stopPropagation(); go(1); }} aria-label={galleryUi.next[language]} data-testid="gallery-lightbox-next"
        className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-[#c9a227] text-white hover:text-[#121212] flex items-center justify-center transition-colors z-10">
        <ChevronRight size={24} />
      </button>}
      <figure key={index} className="max-w-[92vw] animate-lightbox-in" onClick={(e) => e.stopPropagation()}>
        <img src={photo.src} alt={photo.caption[language]} className="max-h-[80vh] max-w-full mx-auto rounded-lg shadow-2xl object-contain" data-testid="gallery-lightbox-image" />
        <figcaption className="text-center mt-4">
          <p className="font-serif text-xl text-[#e8d48b]">{photo.caption[language]}</p>
          <p className="text-white/60 text-sm mt-1">{photo.sub[language]}</p>
          <p className="text-white/30 text-xs mt-2 tracking-widest">{n > 1 ? `${index + 1} / ${n}` : ''}</p>
        </figcaption>
      </figure>
    </div>,
    document.body
  );
};

export default GalleryLightbox;
